import StatusChip, { ChipType } from 'components/@extended/StatusChip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import { FieldLabel } from 'components/ui/field';
import { Textarea } from 'components/ui/textarea';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import React, { useId, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAcknowledgeComplianceBlockMutation } from 'store/api/contacts';
import { ComplianceBlockActiveRuleDto, ComplianceBlockingSeverity, ComplianceBlockStatus } from 'types/compliance';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

type BlockFilter = 'cases' | 'timecards' | 'aml-timecards' | 'all';

interface ComplianceActiveRulesProps {
    activeRules: ComplianceBlockActiveRuleDto[];
    partyId: number;
    filter: BlockFilter;
}

const severityChipType: Record<ComplianceBlockingSeverity, ChipType> = {
    [ComplianceBlockingSeverity.CRITICAL]: 'critical',
    [ComplianceBlockingSeverity.HIGH]: 'error',
    [ComplianceBlockingSeverity.MEDIUM]: 'warning',
    [ComplianceBlockingSeverity.LOW]: 'info',
};

const matchesFilter = (rule: ComplianceBlockActiveRuleDto, filter: BlockFilter): boolean => {
    if (filter === 'all') return true;
    if (filter === 'cases') return rule.blocksCases;
    if (filter === 'timecards') return rule.blocksTimecards;
    return rule.blocksAmlTimecards;
};

const ComplianceActiveRules: React.FC<ComplianceActiveRulesProps> = ({ activeRules, partyId, filter }) => {
    const filteredRules = activeRules.filter((rule) => matchesFilter(rule, filter));

    if (filteredRules.length === 0) {
        return null;
    }

    return (
        <div className="mt-1 flex flex-wrap gap-1">
            {filteredRules.map((rule) => (
                <ComplianceActiveRuleRow key={rule.id} rule={rule} partyId={partyId} />
            ))}
        </div>
    );
};

export default ComplianceActiveRules;

interface ComplianceActiveRuleRowProps {
    rule: ComplianceBlockActiveRuleDto;
    partyId: number;
}

const ComplianceActiveRuleRow: React.FC<ComplianceActiveRuleRowProps> = ({ rule, partyId }) => {
    const { openModal, closeModal } = useModal();
    const [acknowledgeBlock, { isLoading }] = useAcknowledgeComplianceBlockMutation();

    const isPending = rule.status === ComplianceBlockStatus.PENDING_CONFIRMATION;

    const handleAcknowledge = () => {
        openModal(
            <AcknowledgeBlockModal
                rule={rule}
                onCancel={closeModal}
                onConfirm={async (comment) => {
                    await acknowledgeBlock({ partyId, blockId: rule.id, comment: comment || undefined });
                    closeModal();
                }}
            />
        );
    };

    return (
        <div className="inline-flex items-center gap-1">
            <StatusChip
                label={t(`compliance.blocking-rule.${rule.ruleCode}`)}
                size="small"
                type={severityChipType[rule.severity]}
            />
            {isPending && (
                <>
                    <StatusChip
                        label={t('contacts.blocking.rule-status.pending-confirmation')}
                        size="small"
                        type="warning"
                    />
                    <Button
                        {...buttonStyle(
                            { variant: 'outlined', size: 'extraSmall' },
                            'h-[22px] text-[11px] new:text-caption'
                        )}
                        onClick={handleAcknowledge}
                        disabled={isLoading}
                    >
                        <FormattedMessage id="contacts.blocking.acknowledge" />
                    </Button>
                </>
            )}
        </div>
    );
};

/* ── Acknowledge Modal ──────────────────────────────────────── */

export interface AcknowledgeBlockModalProps {
    rule: ComplianceBlockActiveRuleDto;
    onCancel: () => void;
    onConfirm: (comment: string) => Promise<void>;
}

export const AcknowledgeBlockModal: React.FC<AcknowledgeBlockModalProps> = ({ rule, onCancel, onConfirm }) => {
    const commentId = useId();
    const [comment, setComment] = useState('');
    const effectiveFormat = useEffectiveDateFormat();
    const triggeredDate = format(new Date(rule.triggeredAt), effectiveFormat);
    const resolvedDate = rule.resolvedAt ? format(new Date(rule.resolvedAt), effectiveFormat) : null;

    return (
        <>
            <ModalHeader title={t('contacts.blocking.acknowledge-confirm-title')} onCancel={onCancel} />
            <div className="p-6">
                <div className="mb-2 flex items-center gap-2">
                    <span className="text-subtitle1">{t(`compliance.blocking-rule.${rule.ruleCode}`)}</span>
                    <StatusChip
                        label={t(`contacts.blocking.severity.${rule.severity.toLowerCase()}`)}
                        size="small"
                        type={severityChipType[rule.severity]}
                    />
                </div>
                <span className={cn('text-caption text-fg-secondary block', resolvedDate ? 'mb-1' : 'mb-4')}>
                    {t('contacts.blocking.triggered-at', { date: triggeredDate })}
                </span>
                {resolvedDate && (
                    <span className="text-caption text-fg-secondary mb-4 block">
                        <FormattedMessage id="contacts.blocking.resolved-at" values={{ date: resolvedDate }} />
                    </span>
                )}
                <p className="text-body1 mb-4">{t('contacts.blocking.acknowledge-confirm-message')}</p>
                <FieldLabel className="text-fg-secondary mb-1 font-normal" htmlFor={commentId}>
                    {t('contacts.blocking.acknowledge-comment')}
                </FieldLabel>
                {/* Grows with its text from two lines up to four, then scrolls. */}
                <Textarea
                    id={commentId}
                    className="bg-paper max-h-[calc(4lh+18px)] min-h-[calc(2lh+18px)]"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />
            </div>
            <ModalFooter
                onCancel={onCancel}
                onSave={() => {
                    void onConfirm(comment);
                }}
                saveButtonTextId="yes"
                cancelButtonTextId="cancel"
            />
        </>
    );
};
